import React, { useState } from "react";
import { faEnvelope } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormattedMessage, useIntl } from "gatsby-plugin-intl";
import { useForm } from "react-hook-form";
import classNames from "classnames";
import Button from "components/Button";
import Label from "components/Label";
import TextField from "components/TextField";
import SelectField from "components/SelectField";
import Recaptcha, { RecaptchaProvider } from "components/Recaptcha";
import envelopeWhite from "icons/envelope-white.svg";
import { sendContactInfo } from "../../services/data-manager";

const classes = {
  field: "w-full p-4 rounded-lg  bg-lightgrey px-5",
  labelClassName: "mb-2 block tracking-wider uppercase ",

  inputsRow: "flex flex-wrap justify-between mb-6 lg:mb-16 last:mb-0",
  inputCol: "w-full md:w-80 mb-6 md:mb-0",

  /* className="w-112 w-7" */
};

const SUCCESS = "success";
const ERROR = "error";

const ContactForm = ({ onClose }) => {
  const { formatMessage } = useIntl();
  const [sendStatus, setSendStatus] = useState(null);

  const { handleSubmit, register, errors } = useForm();
  const {
    firstName: firstNameError,
    lastName: lastNameError,
    email: emailError,
    message: messageError,
    subject: subjectError,
  } = errors;

  const mockOnSubmit = ({ firstName, lastName, ...data }) => {
    const body = {
      name: `${lastName} ${firstName}`,
      ...data,
    };

    const recaptchaToken = window.grecaptcha.getResponse();

    sendContactInfo(body, recaptchaToken)
      .then(() => {
        setSendStatus(SUCCESS);
      })
      .catch(() => setSendStatus(ERROR));
  };

  return (
    <RecaptchaProvider>
      {sendStatus !== SUCCESS && (
        <form className="sm:px-4" onSubmit={handleSubmit(mockOnSubmit)}>
          <div className="flex justify-center items-center h-20 w-20 rounded-full bg-secondary mx-auto mb-10 mt-5">
            <object data={envelopeWhite} aria-label=" " width="27" />
          </div>

          <div className="mb-6">
            <div className={classNames(classes.inputsRow)}>
              <div className={classes.inputCol}>
                <Label
                  htmlFor="lastName"
                  className={classes.labelClassName}
                  labelKey="fields.name"
                  required
                />

                <TextField
                  id="lastName"
                  variant="default"
                  wrapperClassName={classes.fieldWrapper}
                  className={classes.field}
                  placeholder={formatMessage({ id: "fields.name.placeholder" })}
                  inputRef={register({
                    required: formatMessage({ id: "validation.required" }),
                  })}
                  name="lastName"
                  validationError={lastNameError}
                />
              </div>

              <div className={classes.inputCol}>
                <Label
                  htmlFor="firstName"
                  className={classes.labelClassName}
                  labelKey="fields.firstName"
                  required
                />

                <TextField
                  id="firstName"
                  variant="default"
                  wrapperClassName={classes.fieldWrapper}
                  className={classes.field}
                  placeholder={formatMessage({
                    id: "fields.firstName.placeholder",
                  })}
                  inputRef={register({
                    required: formatMessage({ id: "validation.required" }),
                  })}
                  name="firstName"
                  validationError={firstNameError}
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <Label
              htmlFor="email"
              className={classes.labelClassName}
              labelKey="fields.email"
              required
            />

            <TextField
              variant="default"
              wrapperClassName={classes.fieldWrapper}
              className={classNames(classes.field, "")}
              placeholder={formatMessage({ id: "fields.email.placeholder" })}
              inputRef={register({
                required: formatMessage({ id: "validation.required" }),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: formatMessage({ id: "validation.email" }),
                },
              })}
              name="email"
              id="email"
              validationError={emailError}
            />
          </div>

          <div className="mb-6">
            <Label
              htmlFor="subject"
              className={classes.labelClassName}
              labelKey="fields.subject"
              required
            />

            <SelectField
              options={[
                {
                  value: formatMessage({ id: "contact.form.options.1" }),
                  label: formatMessage({ id: "contact.form.options.1" }),
                },
                {
                  value: formatMessage({ id: "contact.form.options.2" }),
                  label: formatMessage({ id: "contact.form.options.2" }),
                },
                {
                  value: formatMessage({ id: "contact.form.options.3" }),
                  label: formatMessage({ id: "contact.form.options.3" }),
                },
              ]}
              variant="default"
              noArrow
              wrapperClassName={classes.fieldWrapper}
              className={classNames(classes.field, "bg-white")}
              placeholder={formatMessage({ id: "fields.subject.placeholder" })}
              id="subject"
              name="subject"
              inputRef={register({
                required: formatMessage({ id: "validation.required" }),
              })}
              validationError={subjectError}
            />
          </div>

          <div className="mb-6">
            <Label
              htmlFor="message"
              className={classes.labelClassName}
              labelKey="fields.message"
              required
            />

            <TextField
              multiline
              id="message"
              variant="default"
              wrapperClassName={classes.fieldWrapper}
              className={classNames(classes.field, "h-20")}
              placeholder={formatMessage({ id: "fields.message.placeholder" })}
              inputRef={register({
                required: formatMessage({ id: "validation.required" }),
              })}
              name="message"
              validationError={messageError}
            />
          </div>

          <div className="flex flex-col">
            <Recaptcha className="flex justify-center mb-6" />
            <Button
              type="submit"
              className="w-full sm:w-80 mx-auto"
              variant="default"
              startIcon={
                // eslint-disable-next-line react/jsx-wrap-multilines
                // <object
                //   className="ltr:mr-6"
                //   data={envelopeWhite}
                //   aria-label=""
                //   width="18"
                // />

                // eslint-disable-next-line react/jsx-wrap-multilines
                <FontAwesomeIcon
                  className="text-2xl "
                  type="regular"
                  icon={faEnvelope}
                />
              }
            >
              <FormattedMessage id="contact.sendMessage" />
            </Button>

            {sendStatus === ERROR && (
              <div className="mt-4 mx-auto text-red-500">
                <FormattedMessage id="contact.send.error" />
              </div>
            )}
          </div>
        </form>
      )}

      {sendStatus === SUCCESS && (
        <div className="m-auto h-112 flex flex-wrap items-center text-center justify-center">
          <div>
            <p className="text-lg text-black w-full">
              <FormattedMessage id="contact.send.success" />
            </p>

            <Button
              onClick={onClose}
              variant="default"
              className="w-54 px-5 mt-4 mx-auto"
            >
              <FormattedMessage id="general.close" />
            </Button>
          </div>
        </div>
      )}
    </RecaptchaProvider>
  );
};

export default ContactForm;
